import React, {
  ReactNode,
  useContext,
  useState,
  useRef,
  useEffect,
} from 'react';
import Image from 'next/image';
import { useMediaQuery } from '@mui/material';
import classnames from 'classnames';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import unitLeftArrow from 'public/images/unit_left_arrow.svg';
import unitRightArrow from 'public/images/unit_right_arrow.svg';
import videoTour from 'public/images/video-tour.svg';
import floorplan from 'public/images/floorplan.svg';
import location from 'public/images/location.svg';
import cityAnimated from 'public/images/city-animated.svg';

import { Position } from 'types';
import { BuildingPageContext } from 'pages/[urlParam]/[urlBuildParam]';
import { UnitPageContext } from 'pages/[urlParam]/[urlBuildParam]/[urlUnitParam]';
import { getBuildingFloorplans } from 'src/utils/buildingUtils';
import { Building, StrapiMediaInfo } from 'apolloClient/types';
import { Listing } from 'apolloClient/types/Units';

import PopUpUnitBuilding from '../PopUpUnitBuilding';
import UnitBuildingLocation from '../UnitBuildingLocation';
import PopUpViewLargePhoto from './PopUpViewLargePhoto';

import { getMediumImageUrlFromImage } from 'lib/getImages';
import { translateUnitStatusBasic } from '../../lib/translateUnitStatusBasic';
import BlurredBgImage from './BlurredBgImage';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { RibbonLabel } from '../RibbonLabel';

type BuildingCarouselProps = {
  images?: StrapiMediaInfo[];
  position?: Position;
  renderHeader: JSX.Element;
  tours?: string[] | string;
  nameButton: string;
};

const getTourLabel = (tourUrl?: string | string[] | null) => {
  if (!tourUrl) return null;
  if (Array.isArray(tourUrl)) return 'video';
  if (
    tourUrl?.includes('youtube') ||
    tourUrl?.includes('youtu.be') ||
    tourUrl?.includes('vimeo')
  )
    return 'video tour';
  if (tourUrl?.includes('matterport')) return '3D Walkthrough';

  return 'virtual tour';
};

const initialPhotosModalState = {
  isOpen: false,
  initialShowedIndex: 0,
};

const ArrowLeft = ({ onClick }: any) => (
  <button
    onClick={onClick}
    className="absolute left-0 top-0 h-full flex items-center px-3 md:px-3.5 xl:px-4 hover:bg-black/10 transition-colors z-10"
    aria-label="Previous"
  >
    <Image
      src={unitLeftArrow}
      alt="Previous"
      width={28}
      height={48}
      className="w-[13px] h-[28px] md:w-[28px] md:h-[48px]"
      priority
    />
  </button>
);

const ArrowRight = ({ onClick }: any) => (
  <button
    onClick={onClick}
    className="absolute right-0 top-0 h-full flex items-center px-3 md:px-3.5 xl:px-4 hover:bg-black/10 transition-colors"
    aria-label="Next"
  >
    <Image
      src={unitRightArrow}
      alt="Next"
      width={28}
      height={48}
      className="w-[13px] h-[28px] md:w-[28px] md:h-[48px]"
      priority
    />
  </button>
);

const SLIDE_PADDING = 16;

const BuildingCarousel: React.FC<BuildingCarouselProps> = ({
  images,
  position,
  renderHeader,
  tours,
  nameButton,
}) => {
  const [photosModalState, setPhotosModalState] = useState(
    initialPhotosModalState
  );
  const [isBuildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [selectedDialogTab, setSelectedDialogTab] = useState<number>(0);
  const [imageDimensions, setImageDimensions] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const isDragging = useRef(false);

  const { building: buildingData } = useContext(BuildingPageContext) || {};
  const { building, listing } = useContext(UnitPageContext) || {};
  const {
    statusCode,
    propertyTypeCode,
    closeDate,
    yearBuilt,
    isShortSale,
    isReo,
  } = listing || {};

  const isPreconstruction = buildingData?.isPreconstruction;
  const floorplansFromBuilding = getBuildingFloorplans(
    buildingData?.floorplans
  );
  const floorplanFromListing = listing?.floorplan;
  const floorPlanExists =
    Boolean(floorplansFromBuilding && floorplansFromBuilding.length > 0) ||
    Boolean(floorplanFromListing);
  const imagesExists = Boolean(images && images.length > 0);
  const isTourExists = Boolean((tours?.length || 0) > 0);
  const isPropertypanoramaTour = tours?.includes('propertypanorama');

  const unitLabels =
    propertyTypeCode &&
    statusCode &&
    translateUnitStatusBasic(propertyTypeCode, statusCode, closeDate);

  const isUnitPreconstruction =
    building?.isPreconstruction || Number(yearBuilt) > new Date().getFullYear();

  const extraUnitLabels = (() => {
    if (!building?.isPreconstruction && isReo) {
      return `Foreclosure`;
    } else if (!building?.isPreconstruction && isShortSale) {
      return `Short sale`;
    } else if (isUnitPreconstruction) {
      return `Pre-construction`;
    } else {
      return null;
    }
  })();

  const underContractLabels = (() => {
    if (statusCode === 'PS') {
      return `Under Contract`;
    } else {
      return null;
    }
  })();

  const isUnitLabelLongName = statusCode === 'R' || statusCode === 'CS';

  function closePhotosModal() {
    setPhotosModalState(initialPhotosModalState);
  }

  const openBuildingDialog = (tab: number): void => {
    closePhotosModal();
    setBuildingDialogOpen(true);
    setSelectedDialogTab(tab);
  };

  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1279px)');
  const isDesktop = useMediaQuery('(min-width: 1280px)');

  const areImageDimensionsSet =
    Object.keys(imageDimensions).length >= (images?.length || 0);

  const settings = {
    dots: false,
    infinite: true,
    speed: isMobile ? 200 : 500,
    slidesToScroll: 1,
    prevArrow: <ArrowLeft />,
    nextArrow: <ArrowRight />,
    variableWidth: true,
    centerMode: false,
    beforeChange: () => (isDragging.current = true),
    afterChange: () => (isDragging.current = false),
    adaptiveHeight: true,
  };

  let sliderContainerHeight: number;
  if (isMobile) {
    sliderContainerHeight = 333;
  } else if (isTablet) {
    sliderContainerHeight = 352;
  } else if (isDesktop) {
    sliderContainerHeight = 445;
  }

  const getScaledWidth = (
    naturalWidth: number,
    naturalHeight: number,
    containerHeight: number
  ) => {
    const aspectRatio = naturalWidth / naturalHeight;
    const scaledWidth = Math.round(containerHeight * aspectRatio);
    return scaledWidth + SLIDE_PADDING;
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && areImageDimensionsSet) {
      // Force a resize event to ensure the slider is updated
      // We do it in useEffect to ensure it is executed on the client side
      window.dispatchEvent(new Event('resize'));
    }
  }, [areImageDimensionsSet]);

  // This is a workaround to ensure the slider is visible after the images are loaded
  // And after we recalculate the width of all the slides
  useEffect(() => {
    const sliderContainer = document.querySelector('.slider-container');
    function updateSliderVisibility() {
      if (sliderContainer) {
        sliderContainer.className = classNames('slider-container', {
          'slider-loaded': areImageDimensionsSet || images?.length === 1,
        });
      }
    }
    updateSliderVisibility();
  }, [areImageDimensionsSet, images?.length]);

  const router = useRouter();

  // Because we rely on the image dimension array to determine if the images are loaded,
  // we need to reset the image dimensions when the route changes
  useEffect(() => {
    const handleRouteChange = () => {
      setImageDimensions({});
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      <section className="px-0 mt-[21px] md:mt-5 lg:mt-[10px] text-14-26-0.3 xxxl:container lg:pt-1 lg:pb-2">
        <div className="relative h-[333px] md:h-[352px] xl:h-[445px]">
          {isPreconstruction && (
            <div
              className={classnames(
                'absolute uppercase left-0 md:left-3 xl:left-4 z-40',
                {
                  'top-8': imagesExists,
                  'top-13': !imagesExists,
                }
              )}
            >
              <RibbonLabel label="Pre-construction" type="golden" />
            </div>
          )}
          {unitLabels && (
            <div
              className={classnames(
                'absolute uppercase left-0 md:left-3 xl:left-4 z-40',
                {
                  'top-2 sm:top-[30px]': underContractLabels,
                  'top-[14px] md:top-[30px]': !underContractLabels,
                }
              )}
            >
              <RibbonLabel
                label={unitLabels}
                type="golden"
                statusCode={statusCode as 'R' | 'CS'}
              />
            </div>
          )}
          {extraUnitLabels && (
            <div
              className={classnames(
                'absolute font-sohneBreitHalbfett uppercase left-45 md:left-56 z-40',
                {
                  'ml-2 sm:ml-8 md:ml-10 xl:ml-12':
                    isUnitPreconstruction && isUnitLabelLongName,
                  'ml-8 md:ml-10 xl:ml-12':
                    isUnitLabelLongName && !isUnitPreconstruction,
                  'top-2 sm:top-[30px]': underContractLabels,
                  'top-[30px]': !underContractLabels && !isMobile,
                  'top-[14px]': isMobile,
                }
              )}
            >
              <RibbonLabel
                label={extraUnitLabels}
                type="biancone"
                isUnitPreconstruction={isUnitPreconstruction}
              />
            </div>
          )}
          {underContractLabels && (
            <div
              className={classnames(
                'absolute font-sohneBreitHalbfett uppercase top-[9px] sm:top-[30px] z-40 left-[140px]',
                {
                  'sm:ml-10 md:ml-24':
                    isUnitLabelLongName && !isUnitPreconstruction,
                  'sm:ml-2 md:ml-28':
                    isUnitPreconstruction && !isUnitLabelLongName,
                  'sm:left-96 md:ml-16': extraUnitLabels,
                  'sm:ml-9 md:ml-36':
                    isUnitPreconstruction && isUnitLabelLongName,
                  'sm:left-48 md:left-56': !extraUnitLabels,
                }
              )}
            >
              <RibbonLabel
                label={underContractLabels}
                type="golden"
                width="w-[177px] md:w-[230px]"
              />
            </div>
          )}
          {imagesExists ? (
            <>
              {!areImageDimensionsSet && images?.length !== 1 && (
                <div
                  className={`absolute inset-0 flex items-center justify-center z-50 loading-animation opacity-20 p-2.5 ${
                    isMobile
                      ? 'h-[333px]'
                      : isTablet
                      ? 'h-[352px]'
                      : 'h-[445px]'
                  }`}
                >
                  <Image
                    src={cityAnimated}
                    alt="Loading animation"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <div className="slider-container w-full h-full [&_.slick-slide]:pr-4">
                {images?.length === 1 ? (
                  <div
                    onClick={() =>
                      setPhotosModalState({
                        isOpen: true,
                        initialShowedIndex: 0,
                      })
                    }
                    className="relative w-full h-full"
                  >
                    {images[0] && (
                      <BlurredBgImage
                        src={getMediumImageUrlFromImage(images[0])}
                        blurDataURL={getMediumImageUrlFromImage(images[0])}
                        alt={`${building?.name || buildingData?.name} image`}
                      />
                    )}
                  </div>
                ) : (
                  <Slider {...settings}>
                    {images?.reduce<Array<ReactNode>>((acc, image, index) => {
                      const imageUrl = getMediumImageUrlFromImage(image);
                      if (imageUrl) {
                        acc.push(
                          <div
                            key={index}
                            onClick={(e) => {
                              if (!isDragging.current) {
                                setPhotosModalState({
                                  isOpen: true,
                                  initialShowedIndex: index,
                                });
                              }
                            }}
                            style={{
                              width: imageDimensions[imageUrl]
                                ? getScaledWidth(
                                    imageDimensions[imageUrl].width,
                                    imageDimensions[imageUrl].height,
                                    isMobile ? 333 : isTablet ? 352 : 445
                                  ) + 'px'
                                : 'auto',
                            }}
                            className="relative inline-block h-[333px] md:h-[352px] xl:h-[445px]"
                          >
                            <Image
                              src={imageUrl}
                              alt={`${
                                building?.name || buildingData?.name
                              } image #${index + 1}`}
                              fill
                              priority={index === 0}
                              className="object-contain h-full w-auto object-left"
                              style={{ maxWidth: 'unset' }}
                              onLoadingComplete={(result) => {
                                setImageDimensions((prev) => {
                                  if (!prev[imageUrl]) {
                                    return {
                                      ...prev,
                                      [imageUrl]: {
                                        width: result.naturalWidth,
                                        height: result.naturalHeight,
                                      },
                                    };
                                  }
                                  return prev;
                                });
                              }}
                            />
                          </div>
                        );
                      }
                      return acc;
                    }, [])}
                  </Slider>
                )}
              </div>
            </>
          ) : (
            <div className="z-50 h-[220px] md:h-88 lg:h-112">
              <UnitBuildingLocation position={position} />
            </div>
          )}
          <div className="absolute w-full -translate-x-1/2 md:left-auto md:w-96 transform-none bottom-[-43px] md:bottom-4 lg:bottom-auto md:right-20 lg:top-8 z-10">
            <div className="container z-40 flex justify-center w-auto px-6 pt-0 space-x-2 outline-none md:pl-10 md:space-x-0">
              {isTourExists && !isPropertypanoramaTour && (
                <button
                  onClick={() => openBuildingDialog(3)}
                  className={`${
                    (floorPlanExists || position) && 'md:border-r-0'
                  } z-30 flex items-center justify-center min-w-[120px] w-auto md:min-w-[150px] h-[28px] xl:h-[34px] px-2 py-1 uppercase bg-white border-2 md:w-40 hover-animation group border-gold-dark md:px-3 md:space-x-2 xl:pb-[7px] xl:pt-[5px]`}
                >
                  <span className="hidden hover-animation group-hover:opacity-70 md:inline-block pt-[5px]">
                    <Image src={videoTour} alt="videoTour" />
                  </span>
                  <span className="hover-animation whitespace-nowrap group-hover:opacity-70 text-12-21-0.3 md:text-14-21-0.3">
                    {getTourLabel(tours)}
                  </span>
                </button>
              )}
              {floorPlanExists && (
                <button
                  onClick={() => openBuildingDialog(1)}
                  className={classnames(
                    'flex items-center justify-center min-w-[111px] md:min-w-[153px] px-2 py-1 h-[28px] xl:h-[34px] xl:pb-[7px] xl:pt-[5px] uppercase bg-white border-2 md:w-36 hover-animation group border-gold-dark md:px-3 md:space-x-2',
                    {
                      'md:border-r-0': position,
                    }
                  )}
                >
                  <div className="hidden pt-[5px] hover-animation group-hover:opacity-70 md:inline-block">
                    <Image src={floorplan} alt="floorplans" />
                  </div>
                  <span className="hover-animation group-hover:opacity-70 text-12-21-0.3 md:text-14-14-0.3">
                    {nameButton}
                  </span>
                </button>
              )}
              {position && (
                <button
                  onClick={() => openBuildingDialog(2)}
                  className="flex items-center justify-center min-w-[91px] md:min-w-[125px] lg:min-w-[135px] px-2 py-1 h-[28px] xl:h-[34px] xl:pb-[7px] xl:pt-[5px] uppercase bg-white border-2 md:w-36 hover-animation group border-gold-dark md:px-0 md:space-x-2"
                >
                  <div className="hidden pt-[5px] h-[28px] w-[18px] hover-animation group-hover:opacity-70 md:inline-block">
                    <Image
                      src={location}
                      alt="location"
                      height={20}
                      width={20}
                    />
                  </div>
                  <div className="hover-animation group-hover:opacity-70 text-12-21-0.3 md:text-14-14-0.3 lg:!ml-[6px]">
                    Directions
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className="md:container mt-[58px] md:mt-20 text-m md:hidden">
        <div className="w-fill h-[1px] bg-beige" />
      </section>
      <PopUpViewLargePhoto
        open={photosModalState.isOpen}
        onClose={closePhotosModal}
        images={images}
        actionHandle={() => openBuildingDialog(0)}
        renderHeader={renderHeader}
        initialActiveIndex={photosModalState.initialShowedIndex}
      />
      <PopUpUnitBuilding
        entityType={listing ? 'listing' : 'building'}
        entity={(buildingData as Building) || (listing as Listing)}
        toursButtonLabel={getTourLabel(tours)}
        floorplanButtonLabel={listing ? 'Floor plan' : 'Floor plans'}
        open={isBuildingDialogOpen}
        onClose={() => setBuildingDialogOpen(false)}
        openPopupLarge={setPhotosModalState}
        activeTab={selectedDialogTab}
        images={images}
        tours={tours}
        position={position}
        tabsRenderExclude={{
          photos: !imagesExists,
          floorPlan: !floorPlanExists,
          tour: !isTourExists,
          directions: !position,
        }}
        renderHeader={renderHeader}
      />
    </>
  );
};

export default BuildingCarousel;
